import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { rxObjectify } from 'osn-shared';
import { combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { RootAppState } from '../app.reducer';
import { selectCxbCountries, selectCxbFeeds, selectCxbPlatforms, selectCxbUserSegments, selectGenres } from '../app.selectors';
import { PageVersion } from '../domain/page-version';
import {
  mapApiAppLandingPageDetailsToDomain,
  mapApiContentPageDetailsToDomain,
  mapApiGenrePageDetailsToDomain,
  mapApiMarketingDetailsPageDetailsToDomain,
  mapApiMarketingLandingPageDetailsToDomain,
  mapApiRedeemPageDetailsToDomain,
  UnionPageVersionDetailApiModel,
  VersionedPageMappingDependencies,
} from './mapping/map-api-page-version-to-domain';
import { GenrePageVersionDataService, PageVersionDataService, RedeemPageVersionDataService } from '@cxb/editor-stb-api';
import { PageType } from './page-type';
import { Tenant } from '../domain/tenant';

@Injectable({ providedIn: 'root' })
export class PageVersionApiService {
  constructor(
    private readonly pageVersionDataService: PageVersionDataService,
    private readonly genrePageVersionDataService: GenrePageVersionDataService,
    private readonly redeemPageVersionDataService: RedeemPageVersionDataService,
    private readonly rootStore: Store<RootAppState>
  ) {}

  public loadVersionedPage$(
    id: string,
    pageType: PageType,
    userSegments: string[] | undefined,
    countryCodes: string[] | undefined,
    selectedTenant: Tenant | undefined
  ): Observable<PageVersion> {
    const _userSegments = userSegments?.length > 0 ? userSegments.join(',') : undefined;
    const _countryCodes = countryCodes?.length > 0 ? countryCodes.join(',') : undefined;
    switch (pageType) {
      case PageType.content:
        return this.getMappedPage$(
          this.pageVersionDataService.getPageVersion(
            id,
            undefined,
            undefined,
            undefined,
            _userSegments,
            undefined,
            _countryCodes,
            selectedTenant?.id
          ),
          pageType,
          mapApiContentPageDetailsToDomain
        );
      case PageType.genre:
        return this.getMappedPage$(
          this.genrePageVersionDataService.getGenrePage(id, _userSegments, _countryCodes, selectedTenant?.id),
          pageType,
          mapApiGenrePageDetailsToDomain
        );
      case PageType.landing:
        return this.getMappedPage$(
          this.pageVersionDataService.getLandingPage(id, _userSegments, _countryCodes, selectedTenant?.id),
          pageType,
          mapApiMarketingLandingPageDetailsToDomain
        );
      case PageType.marketingDetails:
        return this.getMappedPage$(
          this.pageVersionDataService.getDetailPage(id, _userSegments, _countryCodes, selectedTenant?.id),
          pageType,
          mapApiMarketingDetailsPageDetailsToDomain
        );
      case PageType.redeem:
        return this.getMappedPage$(
          this.redeemPageVersionDataService.getRedeemPage(id, _userSegments, _countryCodes, selectedTenant?.id),
          pageType,
          mapApiRedeemPageDetailsToDomain
        );
      case PageType.appLandingAppleAndroidTv:
      case PageType.appLandingCotv:
      case PageType.appLandingMobile:
      case PageType.appLandingTablet:
        return this.getMappedPage$(
          this.pageVersionDataService.getAppLandingPage(id, _userSegments, _countryCodes, selectedTenant?.id),
          pageType,
          mapApiAppLandingPageDetailsToDomain
        );
    }
  }

  private getMappedPage$<T extends UnionPageVersionDetailApiModel>(
    page$: Observable<T>,
    pageType: PageType,
    mapperFn: (page: T, deps: VersionedPageMappingDependencies) => PageVersion
  ): Observable<PageVersion> {
    return combineLatest([
      this.rootStore.select(selectCxbCountries),
      this.rootStore.select(selectCxbUserSegments),
      this.rootStore.select(selectGenres),
      this.rootStore.select(selectCxbFeeds),
      this.rootStore.select(selectCxbPlatforms),
      page$,
      of(pageType),
    ])
      .pipe(rxObjectify('countries', 'userSegments', 'genres', 'feeds', 'platforms', 'page', 'pageType'))
      .pipe(map((result) => mapperFn(result.page, result)));
  }
}
